import type { IPlayerUpdateEventPayload, IWebOtaUpdateEventPayload } from '@lucimark/shared/models/player-update'
import type { TWebOtaApplyTiming } from '@lucimark/shared/models/tenant'
import type { AxiosInstance, AxiosResponse } from 'axios'
import type { TAuthenticationService } from './AuthenticationService'
import { parsePlayerAuthErrorBody } from '@lucimark/shared/models/player-auth-error'
import Axios, { AxiosError } from 'axios'
import Emittery from 'emittery'
import { attach as retryAttach } from 'retry-axios'
import { resolveAndroidDistribution } from '@/helpers/androidDistribution'
import { getEmbeddedPlayerAppVersion } from '@/helpers/embeddedPlayerAppVersion'

export interface IWebSocketClient {
  authenticate: () => Promise<IAuthenticationContext>
  connect: () => Promise<void>
  sendMessage: (msg: string) => Promise<void>
}

export enum WebSocketClientState {
  DISCONNECTED = 'DISCONNECTED',
  AUTHENTICATING = 'AUTHENTICATING',
  AUTHENTICATED = 'AUTHENTICATED',
  CONNECTING = 'CONNECTING',
  CONNECTED = 'CONNECTED',
  ERROR = 'ERROR'
}

export interface IAuthenticationContext {
  playerId: string
  tenantId: string | null
  update: IPlayerUpdateEventPayload | null
  webOta: IWebOtaUpdateEventPayload | null
  /** Present when registered; drives Web OTA defer-until-restart policy. */
  webOtaApplyTiming?: TWebOtaApplyTiming
}

export interface IWebSocketClientParams {
  /** Platform for x-player-platform header; can be a getter so it's resolved after initPlatform(). */
  platform: string | (() => string)
  /** Sent as x-player-version; may be async (native + embedded merge on Tauri). */
  getAppVersion: () => string | Promise<string>
  /** Web bundle version (e.g. OTA git hash or web build commit). Optional getter; sent as x-player-web-version when present. */
  getWebVersion?: () => string | null | Promise<string | null>
  mode: string
  /** API base URL; can be a getter so it's resolved after initPlatform() (e.g. Android 10.0.2.2). */
  baseURL: string | (() => string)
  authenticationService: TAuthenticationService
}

export class AuthenticationError extends Error {
  constructor() {
    super()
    this.name = 'AuthenticationError'
  }
}

export enum WebSocketClientEvent {
  STATE_UPDATE = 'state-update',
  MESSAGE = 'message',
  DISCONNECTED = 'disconnected',
  RTT = 'rtt',
  AUTHENTICATION_ERROR = 'authentication-error'
}

export interface IWebSocketAuthenticationErrorPayload {
  code?: string
  status?: number
}

export interface WebSocketClientEventMap {
  [WebSocketClientEvent.STATE_UPDATE]: WebSocketClientState
  [WebSocketClientEvent.MESSAGE]: string
  [WebSocketClientEvent.DISCONNECTED]: { code: number, reason: string, wasClean: boolean }
  [WebSocketClientEvent.RTT]: number
  [WebSocketClientEvent.AUTHENTICATION_ERROR]: IWebSocketAuthenticationErrorPayload
}

export function getAxiosInstance(baseURL: string): AxiosInstance {
  const instance = Axios.create({
    baseURL,
    timeout: 1200000
  })

  instance.interceptors.request.use((config) => {
    // config.timeout = 10000 // Wait for 10 seconds before timing out
    return config
  })

  instance.interceptors.response.use(
    (response: AxiosResponse) => response,
    (error) => {
      throw error
    }
  )

  instance.defaults.raxConfig = {
    retry: 8,
    backoffType: 'exponential'
  }

  retryAttach(instance)
  return instance
}

export default class WebSocketClient extends Emittery<WebSocketClientEventMap> implements IWebSocketClient {
  private _getPlatform: () => string
  private _getAppVersion: () => string | Promise<string>
  readonly mode: string
  private _getBaseURL: () => string
  private _axios: AxiosInstance | null = null
  readonly authenticationService: TAuthenticationService
  private pingTimeout: ReturnType<typeof setTimeout> | null = null
  private watchdog: ReturnType<typeof setTimeout> | null = null
  private pingPeriodMs: number = 10000
  private socketTimeoutMs: number = 30000
  private _poolingPeriodMs: number = 5000
  private token: string | null = null
  private ws: WebSocket | null = null
  private _lastPing: number = -1
  private _rtt: number = -1

  private _getWebVersion: (() => string | null | Promise<string | null>) | null = null

  constructor(params: IWebSocketClientParams) {
    super()
    const { platform, getAppVersion, getWebVersion, mode, baseURL, authenticationService } = params
    this._getPlatform = typeof platform === 'function' ? platform : () => platform
    this._getAppVersion = getAppVersion
    this._getWebVersion = getWebVersion ?? null
    this.mode = mode
    this._getBaseURL = typeof baseURL === 'function' ? baseURL : () => baseURL
    this.authenticationService = authenticationService
  }

  get baseURL(): string {
    return this._getBaseURL()
  }

  private get axios(): AxiosInstance {
    if (this._axios == null) {
      this._axios = getAxiosInstance(this._getBaseURL())
    }
    return this._axios
  }

  get poolingPeriodMs() {
    return this._poolingPeriodMs
  }

  get rtt() {
    return this._rtt
  }

  set rtt(rtt: number) {
    this._rtt = rtt
    void this.emit(WebSocketClientEvent.RTT, this._rtt)
  }

  private async sendPing() {
    if (this.watchdog !== null) {
      clearTimeout(this.watchdog)
    }
    this.watchdog = null

    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send('ping')
    }

    this._lastPing = new Date().getTime()
    this.watchdog = setTimeout(() => {
      this.rtt = Number.NaN
      console.warn('pong timeout...')
      void this.disconnect()
    }, this.socketTimeoutMs)
  }

  private receivePong() {
    const now = new Date().getTime()
    this.rtt = now - this._lastPing
    if (this.watchdog !== null) {
      clearTimeout(this.watchdog)
      this.watchdog = null
    }

    this.pingTimeout = setTimeout(() => {
      void this.sendPing()
    }, this.pingPeriodMs)
  }

  async sendMessage(msg: string) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(msg)
    }
    else {
      console.warn(`dropped websocket message ${msg}, still connecting: ${this.ws?.readyState}`)
    }
  }

  async authenticate(): Promise<IAuthenticationContext> {
    void this.emit(WebSocketClientEvent.STATE_UPDATE, WebSocketClientState.AUTHENTICATING)
    const { authenticationService } = this
    const platform = this._getPlatform()
    const appVersion = await Promise.resolve(this._getAppVersion())
    const playerId = await authenticationService.getPlayerId()
    const registration = await authenticationService.getRegistration()
    const headers: Record<string, string> = {
      'x-player-id': playerId,
      'x-player-platform': platform,
      'x-player-version': appVersion,
      'X-Requested-With': 'com.lucimark.player'
    }
    const embeddedAppVersion = getEmbeddedPlayerAppVersion()
    if (embeddedAppVersion !== '') {
      headers['x-player-embedded-version'] = embeddedAppVersion
    }
    const webVersionRaw = this._getWebVersion != null ? await Promise.resolve(this._getWebVersion()) : null
    if (webVersionRaw != null && webVersionRaw !== '') {
      headers['x-player-web-version'] = webVersionRaw
    }
    if (platform === 'android') {
      const distribution = await resolveAndroidDistribution()
      if (distribution != null) {
        headers['x-player-android-distribution'] = distribution
      }
    }
    // Only send tenant auth when registered; omit x-tenant-id when null/empty so API accepts handshake (optional header).
    const tenantId = registration?.tenantId ?? null
    if (tenantId != null && tenantId !== '') {
      Object.assign(headers, {
        'x-tenant-id': tenantId,
        'authorization': `Bearer ${await this.authenticationService.getBearerToken()}`
      })
    }

    try {
      const b64PlayerId = btoa(await this.authenticationService.getPlayerId())
      const data = await this.axios
        .post<{
        token: string
        pingPeriodMs: number
        socketTimeoutMs: number
        update?: IPlayerUpdateEventPayload
        webOta?: IWebOtaUpdateEventPayload
        webOtaApplyTiming?: TWebOtaApplyTiming
      }>(`/players/${b64PlayerId}/ws`, null, { headers })
        .then(({ data }) => data)
      const { token, pingPeriodMs, socketTimeoutMs, update = null, webOta = null, webOtaApplyTiming } = data
      void this.emit(WebSocketClientEvent.STATE_UPDATE, WebSocketClientState.AUTHENTICATED)
      this.pingPeriodMs = pingPeriodMs
      this.socketTimeoutMs = socketTimeoutMs
      this.token = token
      const output: IAuthenticationContext = {
        playerId,
        tenantId: registration?.tenantId ?? null,
        update: update ?? null,
        webOta: webOta ?? null,
        ...(webOtaApplyTiming !== undefined ? { webOtaApplyTiming } : {})
      }
      return output
    }
    catch (error) {
      void this.emit(WebSocketClientEvent.STATE_UPDATE, WebSocketClientState.ERROR)
      if (error instanceof AxiosError) {
        const { status } = error
        if (status === 403 || status === 401) {
          if (typeof registration?.tenantId === 'string') {
            console.warn(`Invalid tenantId for this player: ${registration.tenantId}`)
          }
          const data = error.response?.data
          const code = typeof data === 'object' && data != null
            ? parsePlayerAuthErrorBody(data)?.code
            : undefined
          void this.emit(WebSocketClientEvent.AUTHENTICATION_ERROR, { code, status: status ?? undefined })
          throw new AuthenticationError()
        }
      }
      throw error
    }
  }

  async connect() {
    if (this.token === null) {
      throw new Error('not connected')
    }
    const b64PlayerId = btoa(await this.authenticationService.getPlayerId())
    void this.emit(WebSocketClientEvent.STATE_UPDATE, WebSocketClientState.CONNECTING)
    const url = new URL(this._getBaseURL())
    url.protocol = url.protocol === 'https:' ? 'wss' : 'ws'
    const basePath = url.pathname.replace(/\/$/, '') || ''
    url.pathname = `${basePath}/players/${b64PlayerId}/ws/${this.token}`

    // Supersede any prior socket: null first so its close handler does not tear down the replacement.
    const previous = this.ws
    if (previous != null) {
      this.ws = null
      if (this.pingTimeout) {
        clearTimeout(this.pingTimeout)
        this.pingTimeout = null
      }
      if (this.watchdog) {
        clearTimeout(this.watchdog)
        this.watchdog = null
      }
      try {
        const previousClosingOrClosed = ([WebSocket.CLOSED, WebSocket.CLOSING] as number[])
          .includes(previous.readyState)
        if (!previousClosingOrClosed) {
          previous.close()
        }
      }
      catch {
        // ignore
      }
    }

    const socket = new WebSocket(url.toString())
    this.ws = socket

    socket.addEventListener('open', () => {
      if (this.ws !== socket) {
        return
      }
      if (socket.readyState === WebSocket.OPEN) {
        void this.emit(WebSocketClientEvent.STATE_UPDATE, WebSocketClientState.CONNECTED)
        void this.sendPing()
      }
    })

    socket.addEventListener('close', (event: CloseEvent) => {
      // Ignore close from a socket that was replaced by a newer connect().
      if (this.ws !== socket) {
        return
      }
      this.clearPingTimers()
      this.ws = null
      void this.emit(WebSocketClientEvent.STATE_UPDATE, WebSocketClientState.DISCONNECTED)
      const { code, reason, wasClean } = event
      void this.emit(WebSocketClientEvent.DISCONNECTED, { code, reason, wasClean })
    })

    socket.addEventListener('message', (event: MessageEvent<any>) => {
      if (this.ws !== socket) {
        return
      }
      const rawMsg = event.data as string
      if (rawMsg === 'pong') {
        this.receivePong()
      }
      else {
        void this.emit(WebSocketClientEvent.MESSAGE, rawMsg)
      }
    })
  }

  private clearPingTimers() {
    if (this.pingTimeout) {
      clearTimeout(this.pingTimeout)
      this.pingTimeout = null
    }
    if (this.watchdog) {
      clearTimeout(this.watchdog)
      this.watchdog = null
    }
  }

  async disconnect() {
    this.clearPingTimers()
    const socket = this.ws
    if (socket == null) {
      return
    }
    const isClosingOrClosed = ([WebSocket.CLOSED, WebSocket.CLOSING] as number[]).includes(socket.readyState)
    if (!isClosingOrClosed) {
      // Keep this.ws until close so the handler can emit DISCONNECTED (reconnect path).
      socket.close()
    }
  }
}
